/* GrowthOS — Sales Order record (view) page.
   Rebuilt to the shared `.gos-leadrec` / `gos-lr-*` detail shell (mirrors the
   Quote record + Lead/Contact/Account/Opportunity/Campaign detail pages): back
   row, header card (code + status badge + subject + meta, owner pill, PDF + Edit
   + dots menu, chips), a horizontal tab bar, a read-only Overview (info cards +
   read-only Line Items + Terms/Description) and the real Activity(Timeline)/Notes/
   Attachments/Emails child tabs. Replaces the legacy left-nav scroll-spy that
   reused SalesForm in "view mode" — SalesForm is now a plain editable form (inputs
   everywhere), so view pages can't reuse it (same reasoning as the other detail pages).
   Real capabilities preserved (same APIs/gating as the legacy page): Download PDF
   (`getSalesOrderPdf`), Edit (locked for delivered/cancelled unless System Manager),
   Convert to Invoice (`/sales/createInvoice` with the exact legacy state payload),
   Send to Customer (`sendEmailForSalesOrderPdf`), and a Locked indicator. */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchAttachmentsAndAppendToModule,
  fetchDetailAndAppendToModule,
  fetchNotesAndAppendToModule,
  fetchSalesOrderedItemAndAppendToModule,
} from '../../redux/actions';
import { getSalesById, getUserRole } from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import apiServices from '../../services/service';
import Loader from '../../shared/component/Loader';
import Notes from '../sharedChild/note/notes';
import Attachments from '../sharedChild/attachment/attachments';
import Email from '../sharedChild/email/emails';
import Timeline from '../sharedChild/timeline';

const ICONS = {
  grid: <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />,
  clock: (<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  note: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM8 13h8M8 17h5" />,
  paperclip: <path d="M21 8l-9 9a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8" />,
  mail: <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7" />,
  link: <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />,
  location: (<><path d="M12 21s-7-6.5-7-11a7 7 0 0 1 14 0c0 4.5-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></>),
  template: <path d="M4 4h16a1 1 0 0 1 1 1v4H3V5a1 1 0 0 1 1-1ZM3 9h7v11H4a1 1 0 0 1-1-1V9ZM10 9h11v6H10zM10 15h11v4a1 1 0 0 1-1 1H10v-5Z" />,
  calendar: <path d="M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />,
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  deal: <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />,
  briefcase: <path d="M3 7h18v13H3zM8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />,
  dots: <path d="M12 5h.01M12 12h.01M12 19h.01" />,
  chevL: <path d="M15 18l-6-6 6-6" />,
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />,
  download: <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />,
  invoice: <path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-5ZM9 13h6M9 17h6" />,
  lock: (<><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>),
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const GOS_AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const gosInitials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
const gosAvaColor = (name) =>
  GOS_AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOS_AVA_COLORS.length];

const CURRENCY = process.env.REACT_APP_CURRENCY || '$';
const money = (n) => `${CURRENCY} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const val = (v) => (v === 0 || v ? v : null);
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
};
const fmtDT = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// SaleOrderStatus is admin-configurable master data — infer a badge tone by substring.
const statusTone = (label) => {
  const s = String(label || '').toLowerCase();
  if (s.includes('cancel') || s.includes('reject') || s.includes('void')) return 'red';
  if (s.includes('deliver')) return 'purple';
  if (s.includes('approv') || s.includes('complet') || s.includes('paid')) return 'green';
  if (s.includes('backorder') || s.includes('hold') || s.includes('pend')) return 'amber';
  if (s.includes('creat') || s.includes('draft') || s.includes('open')) return 'blue';
  return 'gray';
};

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);

const TextCard = ({ title, icon, text }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className={'gos-lr-textbody' + (text ? '' : ' muted')}>{text || 'No content provided.'}</div>
  </div>
);

export const ViewSales = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'salesOrder');
  const [loading, setLoading] = useState(storeLoading);

  const searchParams = new URLSearchParams(location.search);
  const salesID = searchParams.get('id');

  const userRole = useSelector(getUserRole);
  const isSystemManager = (userRole || '') === 'System Manager';

  const [salesLocalData, setSalesLocalData] = useState({ ...(location.state || {}), type: 'view', from: 'view' });
  const salesStoreData = useSelector((state) => getSalesById(state, location.state?.id || salesID));

  useEffect(() => {
    setSalesLocalData((prev) => ({ ...prev, ...salesStoreData }));
  }, [salesStoreData]);

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    const salesId = location.state?.id || salesID;
    if (salesId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'SaleOrder', id: salesId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'SaleOrder', id: salesId }));
      dispatch(fetchAttachmentsAndAppendToModule({ moduleName: 'SaleOrder', id: salesId }));
      dispatch(fetchSalesOrderedItemAndAppendToModule({ moduleName: 'SaleOrder', id: salesId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, salesLocalData?.id]);

  const cd = salesLocalData || {};
  const status = cd.status;
  const items = useMemo(() => (Array.isArray(cd.saleOrderDetails) ? cd.saleOrderDetails : []), [cd.saleOrderDetails]);
  const owner = cd.salesOwner;

  const isLocked = status && ['delivered', 'cancelled'].includes(String(status).toLowerCase());
  const editDisabled = isLocked && !isSystemManager;

  const handleEditSalesOrder = () => {
    navigate(`/sales/editSalesOrder?id=${cd?.id}`, { state: { ...cd, type: 'view', from: 'view' } });
  };

  const handleConvertToInvoice = () => {
    setMenuOpen(false);
    navigate('/sales/createInvoice', {
      state: {
        ...cd,
        invoiceOwner: cd?.salesOwner,
        invoiceOwnerId: cd?.salesOwnerId,
        invoiceDetails: cd?.saleOrderDetails,
        currencyPrice: cd?.currency,
        salesOrderId: cd?.id,
        salesOrder: cd?.subject,
        from: 'QuoteView',
      },
    });
  };

  const downloadPdf = async () => {
    setLoading(true);
    try {
      const cleanedData = {
        ...cd,
        saleOrderDetails: cd.saleOrderDetails?.map(({ createdDateTime, updatedDateTime, ...rest }) => rest) || [],
      };
      delete cleanedData.notesList;
      delete cleanedData.createdDateTime;
      delete cleanedData.updatedDateTime;
      const response = await apiServices.getSalesOrderPdf(cleanedData);
      if (response.status === 200 && response.data instanceof Blob) {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${cd?.code || 'SalesOrder'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Failed to download PDF', { position: 'top-right' });
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('An unexpected error occurred while downloading PDF.', { position: 'top-right' });
    } finally {
      setLoading(false);
    }
  };

  const sendToCustomer = async () => {
    setMenuOpen(false);
    try {
      const cleanedData = {
        ...cd,
        saleOrderDetails: cd.saleOrderDetails?.map(({ createdDateTime, updatedDateTime, ...rest }) => rest) || [],
      };
      if (cleanedData.dueDate == null) delete cleanedData.dueDate;
      if (cleanedData.orderDate == null) delete cleanedData.orderDate;
      const response = await apiServices.sendEmailForSalesOrderPdf(cleanedData);
      if (response.status === 200 && String(response.data || '').toLowerCase() === 'email sent successfully') {
        toast.success(response.data, { position: 'top-right' });
      } else {
        toast.error('Failed to send email.', { position: 'top-right' });
      }
    } catch (error) {
      console.error('Error sending sales order email:', error);
      toast.error('An error occurred while sending email.', { position: 'top-right' });
    }
  };

  const chips = [
    { icon: 'deal', label: 'Grand Total ' + money(cd.grandTotal) },
    { icon: 'grid', label: items.length + ' line item' + (items.length !== 1 ? 's' : '') },
    owner && { icon: 'user', label: 'Owner ' + owner },
    cd.createdDateTime && { icon: 'clock', label: 'Created ' + fmtDT(cd.createdDateTime) },
    isLocked && { icon: 'lock', label: 'Locked', tone: 'blue' },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'activity', label: 'Activity', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: cd.notesList?.length },
    { id: 'attachments', label: 'Attachments', icon: 'paperclip', count: cd.attachments?.length },
    { id: 'emails', label: 'Emails', icon: 'mail' },
  ];

  const addrRows = (p) => [
    ['Street', val(cd[p + 'Street'])],
    ['City', val(cd[p + 'City'])],
    ['State', val(cd[p + 'State'])],
    ['Country', val(cd[p + 'Country'])],
    ['Code', val(cd[p + 'Code'])],
  ];

  const lineItems = (
    <div className="gos-lr-card">
      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <Ic name="grid" size={16} /> Line Items
          <span className="gos-lr-count">{items.length}</span>
        </div>
      </div>
      <div className="gos-qf-items">
        <div className="gos-qf-items-main">
          {items.length === 0 ? (
            <div className="gos-qr-empty">No items added to this sales order.</div>
          ) : (
            <div className="gos-qf-items-scroll">
              <div className="gos-qf-grid gos-qr-lines">
                <div className="gos-qf-hrow">
                  {['#', 'Product', 'Description', 'Qty', 'List Price', 'Total'].map((h, i) => (
                    <div key={i} className={'gos-qf-hcell' + (['Qty', 'List Price', 'Total'].includes(h) ? ' gos-num' : '')}>{h}</div>
                  ))}
                </div>
                {items.map((it, i) => (
                  <div className="gos-qf-row" key={it.id || i}>
                    <div className="gos-qf-c gos-qf-sn">{i + 1}</div>
                    <div className="gos-qf-c" style={{ fontWeight: 550 }}>{it.productName || '—'}</div>
                    <div className="gos-qf-c" style={{ color: 'var(--gos-text-3)' }}>{it.description || '—'}</div>
                    <div className="gos-qf-c gos-num gos-qf-ro">{it.quantity ?? 0}</div>
                    <div className="gos-qf-c gos-num gos-qf-ro">{money(it.listPrice)}</div>
                    <div className="gos-qf-c gos-num gos-qf-total">{money(it.lineTotal ?? it.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="gos-qf-summary">
          <div className="gos-qf-summary-head">Order Summary</div>
          <div className="gos-qf-summary-body">
            <div className="gos-qf-sum-row"><span>Sub Total</span><span className="gos-qf-sum-val">{money(cd.subTotal)}</span></div>
            <div className="gos-qf-sum-row"><span>Discount</span><span className="gos-qf-sum-val">− {money(cd.discountTotal)}</span></div>
            <div className="gos-qf-sum-row"><span>Tax</span><span className="gos-qf-sum-val">+ {money(cd.taxTotal)}</span></div>
            <div className="gos-qf-sum-row"><span>Adjustment</span><span className="gos-qf-sum-val">{money(cd.adjustment)}</span></div>
            <div className="gos-qf-sum-row gos-qf-grand"><span>Grand Total</span><span className="gos-qf-sum-val">{money(cd.grandTotal)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );

  const overview = (
    <>
      <div className="gos-lr-grid2">
        <InfoCard
          title="Sales Order Information"
          icon="note"
          rows={[
            ['Sales Owner', val(owner)],
            ['Subject', val(cd.subject)],
            ['Status', status ? <span className={'gos-badge gos-b-' + statusTone(status)}><span className="gos-pip" />{status}</span> : null],
            ['Order Date', val(fmtDate(cd.orderDate))],
            ['Due Date', val(fmtDate(cd.dueDate))],
            ['Carrier', val(cd.carrier)],
            ['Currency', val(cd.currency)],
          ]}
        />
        <InfoCard
          title="Association With"
          icon="link"
          rows={[
            ['Account Name', val(cd.accountName)],
            ['Opportunity', val(cd.opportunityName)],
            ['Quote Name', val(cd.quoteName)],
            ['Contact Name', val(cd.contactName)],
            ['Created Date', val(fmtDT(cd.createdDateTime))],
          ]}
        />
        <InfoCard
          title="Order Details"
          icon="briefcase"
          rows={[
            ['Purchase Order', val(cd.purchaseOrder)],
            ['Customer No.', val(cd.customerNo)],
            ['Sales Commission', cd.salesCommission ? money(cd.salesCommission) : null],
            ['Excise Duty', cd.exciseDuty ? money(cd.exciseDuty) : null],
            ['Pending', cd.pending ? 'Yes' : 'No'],
          ]}
        />
        <InfoCard title="Billing Address" icon="location" rows={addrRows('billing')} />
        <InfoCard title="Shipping Address" icon="location" rows={addrRows('shipping')} />
      </div>
      <div style={{ marginTop: 16 }}>{lineItems}</div>
      <div className="gos-lr-grid2" style={{ marginTop: 16 }}>
        <TextCard title="Terms and Conditions" icon="template" text={cd.termsAndConditions} />
        <TextCard title="Description" icon="note" text={cd.descriptionInformation} />
      </div>
    </>
  );

  let tabBody = overview;
  if (activeTab === 'activity') tabBody = <Timeline localData={cd} module={'SaleOrder'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={cd} module={'SaleOrder'} />;
  else if (activeTab === 'attachments') tabBody = <Attachments localData={cd} module={'SaleOrder'} />;
  else if (activeTab === 'emails') tabBody = <Email localData={cd} module={'SaleOrder'} />;

  return (
    <>
      {loading && <Loader />}
      <div className="ViewLeadPageOuter gos-leadrec">
        {/* back row */}
        <div className="gos-lr-backrow">
          <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/sales/salesOrder')}>
            <Ic name="chevL" size={15} /> Sales Orders
          </button>
          {cd.code ? (
            <div className="gos-lr-leadid">
              Sales Order <span>{cd.code}</span>
            </div>
          ) : null}
        </div>

        {/* header card */}
        <div className="gos-lr-card gos-lr-header">
          <div className="gos-lr-header-top">
            <div className="gos-lr-header-l">
              <span className="gos-lr-ava" style={{ background: 'var(--gos-accent-soft)', color: 'var(--gos-accent)' }}>
                <Ic name="invoice" size={22} />
              </span>
              <div className="gos-lr-header-meta">
                <div className="gos-lr-name" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {cd.code || 'Sales Order'}
                  {status ? (
                    <span className={'gos-badge gos-b-' + statusTone(status)}>
                      <span className="gos-pip" />
                      {status}
                    </span>
                  ) : null}
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--gos-text)', marginTop: 3 }}>
                  {cd.subject || 'Untitled sales order'}
                </div>
                <div className="gos-lr-subline">
                  <Ic name="building" size={14} />
                  {cd.accountName || '—'}
                  {fmtDate(cd.dueDate) ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      <Ic name="calendar" size={14} />
                      Due {fmtDate(cd.dueDate)}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="gos-lr-actions">
              {owner ? (
                <span className="gos-qr-owner">
                  <span className="gos-lr-ava" style={{ width: 24, height: 24, fontSize: 10.5, background: gosAvaColor(owner) }}>
                    {gosInitials(owner)}
                  </span>
                  {owner}
                </span>
              ) : null}
              <button type="button" className="gos-lr-ghost-btn" onClick={downloadPdf} disabled={loading}>
                <Ic name="download" size={16} /> PDF
              </button>
              <button type="button" className="gos-lr-contact-btn" onClick={handleEditSalesOrder} disabled={editDisabled} title={editDisabled ? 'This sales order is locked and cannot be edited' : 'Edit sales order'}>
                <Ic name="edit" size={16} /> Edit
              </button>
              <button
                type="button"
                className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
              >
                <Ic name="dots" size={18} />
              </button>
              {menuOpen ? (
                <>
                  <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="gos-lr-menu">
                    <button type="button" className="gos-lr-menu-item" onClick={sendToCustomer}>
                      <Ic name="mail" size={15} /> Send to Customer
                    </button>
                    <button type="button" className="gos-lr-menu-item" onClick={handleConvertToInvoice}>
                      <Ic name="invoice" size={15} /> Convert to Invoice
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {chips.length ? (
            <div className="gos-lr-chips">
              {chips.map((c, i) => (
                <span className={'gos-lr-chip' + (c.tone ? ' gos-lr-chip-' + c.tone : '')} key={i}>
                  <Ic name={c.icon} size={14} /> {c.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {/* tabs */}
        <div className="gos-lr-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              <Ic name={t.icon} size={15} /> {t.label}
              {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {/* body */}
        {tabBody}
      </div>
    </>
  );
};

export default ViewSales;
